import { useState } from 'react';
// Pure: reading and surgically rewriting the lists written into the page. The phone's own.
// `readableValue` is for the list; the form keeps `displayValue`, whose text saves back exactly.
import {
  addField, applyDraft, blankRow, displayValue, moveRow, readableValue, removeField, writeSeedCollection,
  type SeedCollection,
} from '@/webapp/content';
// Pure: a field or list name in words — `MENU_ITEMS` is "Menu items". The phone's own.
import { humanize } from '@/webapp/readable';
import { columnExample } from '@/webapp/columnExample';
// The phone's words for each change, kept for Undo last change (src/webapp/chatEdit.ts).
import { MANAGE_CHANGE, manageListChange } from '@/webapp/chatEdit';
import { Button, Icon, TextField } from '../../../ui';
import ConfirmDialog from './ConfirmDialog';

type Rows = Record<string, unknown>[];

/** Long enough that one line would hide most of it: drawn as a box you can read it in. */
const LONG = 40;

/**
 * The lists written into the page itself — the courses, the clubs, the tips — edited as rows,
 * the phone's Content tab in its words (src/webapp/WebAppAdminPanel.tsx).
 *
 * Every change rewrites only the array literal it belongs to (content.ts) and is saved and put
 * on the live page through `onSaveSource`, which is chat's own path. A row, a new field, a field
 * removed, a row moved: each is one save, so the page never holds half of a change.
 *
 * One departure, on purpose: Add item opens an empty form and the row is written when it is
 * saved. The phone writes a blank row at once, so a visitor could open the page between the tap
 * and the save and find an empty card — or keep finding it, if the owner never came back.
 */
export default function ContentSection({
  collections,
  source,
  locked,
  onSaveSource,
}: {
  /** The page's own lists; S.store seeds are sample data and never reach here (see ManagePanel). */
  collections: SeedCollection[];
  /** The source these collections were read from: every rewrite is made to THIS text. */
  source: string;
  /** True while a chat change is on its way: a save now would be refused, or refuse that. */
  locked: boolean;
  /** Saves and publishes; `what` is the change in words, for Undo. Rejects only when nothing was saved. */
  onSaveSource: (next: string, what: string) => Promise<void>;
}) {
  const [editing, setEditing] = useState<{ collection: string; index: number; isNew: boolean } | null>(null);
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [long, setLong] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [newField, setNewField] = useState<string | null>(null);
  const [removing, setRemoving] = useState<{ collection: SeedCollection; field: string } | null>(null);

  function open(c: SeedCollection, index: number, isNew = false): void {
    const row = isNew ? {} : (c.rows[index] ?? {});
    const values: Record<string, string> = {};
    for (const f of c.fields) values[f] = isNew ? '' : displayValue(row[f]);
    setDraft(values);
    setLong(c.fields.filter((f) => values[f].length > LONG));
    setNewField(null);
    setError(null);
    setEditing({ collection: c.name, index, isNew });
  }

  /**
   * Rewrite one collection's rows and save. A structure change (a field added or removed, a row
   * moved) keeps the form open — the owner is usually about to make another — and keeps what
   * they had typed in the fields that are still there, which the save did not include.
   */
  async function commit(
    what: string,
    mutate: (rows: Rows, c: SeedCollection) => Rows,
    options: { stayOpen?: boolean; at?: number } = {},
  ): Promise<boolean> {
    if (!editing || saving || locked) return false;
    const c = collections.find((x) => x.name === editing.collection);
    if (!c) return false;
    setError(null);
    setSaving(true);
    try {
      const next = mutate(c.rows.map((r) => ({ ...r })), c);
      /*
       * Nothing changed — an item opened and saved as it was, a move that could not move. Saving
       * anyway would rewrite the list's formatting and republish the page for no change at all,
       * and leave an Undo in Chat that undoes nothing.
       */
      if (JSON.stringify(next) === JSON.stringify(c.rows)) {
        if (!options.stayOpen) setEditing(null);
        return true;
      }
      await onSaveSource(writeSeedCollection(source, c.name, next), manageListChange(what, c.name));
      if (options.stayOpen) {
        const at = options.at ?? editing.index;
        const row = next[at] ?? {};
        setDraft((typed) => {
          const values: Record<string, string> = {};
          for (const f of Object.keys(row)) if (f !== 'id') values[f] = f in typed ? typed[f] : displayValue(row[f]);
          return values;
        });
        setEditing({ collection: c.name, index: at, isNew: false });
      } else {
        setEditing(null);
      }
      return true;
    } catch (e) {
      setError(e instanceof Error && e.message ? e.message : 'Please try again.');
      return false;
    } finally {
      setSaving(false);
    }
  }

  if (editing) {
    // The list can go from under this form: a chat edit can rename or remove it.
    const c = collections.find((x) => x.name === editing.collection);
    if (!c) {
      return (
        <div className="manage-edit">
          <p className="type-body">That item is no longer here.</p>
          <Button title="Back" variant="ghost" accessibilityLabel="Back to the list" onPress={() => setEditing(null)} />
        </div>
      );
    }
    const original = editing.isNew ? undefined : c.rows[editing.index];
    // What the app already wrote is a better example of the shape it expects than any guess.
    const siblings = c.rows.filter((_, i) => editing.isNew || i !== editing.index);
    const fields = editing.isNew ? c.fields : Object.keys(draft);
    const off = saving || locked;

    const save = () =>
      commit(editing.isNew ? MANAGE_CHANGE.addItem : MANAGE_CHANGE.changeItem, (rows) => {
        const typed: Record<string, string> = {};
        for (const f of fields) typed[f] = draft[f] ?? '';
        if (!editing.isNew) {
          // Every field in the shape its column holds — a date stays a date, a yes stays true.
          rows[editing.index] = { ...original, ...applyDraft(original, typed, siblings) };
          return rows;
        }
        /*
         * A new row: shaped like its neighbours first (a number column gets 0, never "", which
         * the app's arithmetic would turn into NaN), then what was typed on top. A field left
         * empty keeps that shaped blank.
         */
        const filled = Object.fromEntries(Object.entries(typed).filter(([, v]) => v.trim() !== ''));
        rows.push({ ...blankRow(c.rows, c.fields), ...applyDraft(undefined, filled, siblings) });
        return rows;
      });

    return (
      <div className="manage-edit">
        <h3 className="type-heading">{editing.isNew ? 'New item' : 'Edit item'}</h3>
        {fields.map((f) => (
          <div key={f}>
            <TextField
              label={humanize(f)}
              value={draft[f] ?? ''}
              onChangeText={(v) => setDraft((d) => ({ ...d, [f]: v }))}
              placeholder={columnExample(f, siblings)}
              multiline={long.includes(f)}
            />
            {!editing.isNew && fields.length > 1 && (
              <Button
                title="Remove field"
                variant="ghost"
                disabled={off}
                accessibilityLabel={`Remove the ${humanize(f)} field from every item`}
                onPress={() => setRemoving({ collection: c, field: f })}
              />
            )}
          </div>
        ))}

        {!editing.isNew &&
          (newField === null ? (
            <Button
              title="Add a field"
              variant="ghost"
              disabled={off}
              accessibilityLabel="Add a field to every item"
              onPress={() => setNewField('')}
            />
          ) : (
            <div>
              <TextField label="Name for the new field" value={newField} onChangeText={setNewField} placeholder="note" autoFocus />
              <div className="build-actions">
                <Button
                  title="Add field"
                  variant="secondary"
                  disabled={off || !newField.trim()}
                  accessibilityLabel="Add this field"
                  onPress={() => {
                    const name = newField;
                    // Kept open when it fails ("There is already a …"), so the name can be changed.
                    void commit(MANAGE_CHANGE.addField(name), (rows) => addField(rows, name), { stayOpen: true }).then((ok) => ok && setNewField(null));
                  }}
                />
                <Button title="Cancel" variant="ghost" accessibilityLabel="Cancel adding a field" onPress={() => setNewField(null)} />
              </div>
            </div>
          ))}

        {!editing.isNew && (
          <div className="build-actions">
            <Button
              title="Move up"
              variant="ghost"
              disabled={off || editing.index === 0}
              accessibilityLabel="Move this item earlier in the list"
              onPress={() => void commit(MANAGE_CHANGE.moveItem, (rows) => moveRow(rows, editing.index, -1), { stayOpen: true, at: editing.index - 1 })}
            />
            <Button
              title="Move down"
              variant="ghost"
              disabled={off || editing.index >= c.rows.length - 1}
              accessibilityLabel="Move this item later in the list"
              onPress={() => void commit(MANAGE_CHANGE.moveItem, (rows) => moveRow(rows, editing.index, 1), { stayOpen: true, at: editing.index + 1 })}
            />
          </div>
        )}

        {error && (
          <p role="alert" data-error-code="content_save" className="type-caption field-error-text">
            <strong>Couldn&apos;t save.</strong> {error}
          </p>
        )}

        <div className="build-actions">
          <Button
            title="Save and republish"
            loading={saving}
            // A new item with nothing in it would be the empty card this form exists to avoid.
            disabled={locked || (editing.isNew && fields.every((f) => !(draft[f] ?? '').trim()))}
            accessibilityLabel="Save this item and update the live app"
            onPress={() => void save()}
          />
          <Button title="Cancel" variant="ghost" accessibilityLabel="Cancel editing" onPress={() => setEditing(null)} />
        </div>
        {!editing.isNew && (
          <Button
            title="Delete item"
            variant="dangerSoft"
            disabled={off}
            accessibilityLabel="Delete this item"
            onPress={() => void commit(MANAGE_CHANGE.deleteItem, (rows) => rows.filter((_, i) => i !== editing.index))}
          />
        )}

        <ConfirmDialog
          question={
            removing
              ? {
                  title: `Remove "${humanize(removing.field)}"?`,
                  body: `It will be removed from all ${removing.collection.rows.length} ${removing.collection.rows.length === 1 ? 'item' : 'items'}, and what is in it will be lost.`,
                  confirm: 'Remove',
                  cancel: 'Keep it',
                }
              : null
          }
          onCancel={() => setRemoving(null)}
          onConfirm={() => {
            const field = removing?.field;
            setRemoving(null);
            if (field) void commit(MANAGE_CHANGE.removeField(field), (rows) => removeField(rows, field), { stayOpen: true });
          }}
        />
      </div>
    );
  }

  return (
    <div className="manage-list">
      {collections.map((c) => (
        <section key={c.name} aria-label={humanize(c.name)}>
          <h3 className="type-heading">
            {humanize(c.name)} <span className="type-caption project-meta">{c.rows.length}</span>
          </h3>
          {!c.editable ? (
            <p className="type-caption">Built in code, so it can&apos;t be edited as a list.</p>
          ) : (
            <>
              <ul className="project-list">
                {c.rows.map((row, i) => {
                  // Read, not edited: a date as a date, not as the page stores it.
                  const title = readableValue(row[c.fields[0]]);
                  const sub = c.fields[1] ? readableValue(row[c.fields[1]]) : '';
                  return (
                    <li key={i}>
                      <button
                        type="button"
                        className="card clay-card project-row pressable"
                        aria-label={`Edit ${title || `item ${i + 1}`}`}
                        onClick={() => open(c, i)}
                      >
                        <span className="project-text">
                          <span className="type-heading">{title || `Item ${i + 1}`}</span>
                          {sub && <span className="type-caption project-meta">{sub}</span>}
                        </span>
                        <Icon name="chevron" size={18} color="var(--color-chevron)" />
                      </button>
                    </li>
                  );
                })}
              </ul>
              {c.fields.length > 0 && (
                <Button
                  title="Add item"
                  variant="ghost"
                  disabled={locked}
                  accessibilityLabel={`Add an item to ${humanize(c.name)}`}
                  onPress={() => open(c, c.rows.length, true)}
                />
              )}
            </>
          )}
        </section>
      ))}
    </div>
  );
}
